window.quickShopHtmls = {};

class CardQuickShop extends HTMLElement {
  constructor() {
    super();
    this.productSectionId = 'main-product--quick-shop';
    this.abortController = new AbortController();
    this._variationListenerAdded = false;
    this._boundVariationClick = this.handleVariationClick.bind(this);
    this._sizeListenerAdded = false;
    this._boundSizeChange = this.handleSizeChange.bind(this);
  }

  connectedCallback() {
    this.productHandle = this.getAttribute('data-product-handle');
    this.productCard = this.closest('product-card');

    // Prefetch when card enters viewport
    this.observeAndPrefetch();

    // Handle quick shop trigger click within the card
    this.productCard.addEventListener('click', this.handleTriggerClick.bind(this), {
      signal: this.abortController.signal,
    });
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  observeAndPrefetch() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.fetchProductQuickShop(this.productHandle);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.25 }
    );
    observer.observe(this.productCard);
  }

  handleTriggerClick(e) {
    const trigger = e.target.closest('[data-quick-shop-handle]');
    if (!trigger) return;

    e.preventDefault();
    e.stopPropagation();

    this.openDrawer(trigger.dataset.quickShopHandle);
  }

  async openDrawer(productHandle) {
    const drawer = document.getElementById('quick-shop-drawer');
    if (!drawer) return;

    const loading = drawer.querySelector('[data-qs-loading]');
    const content = drawer.querySelector('[data-qs-content]');

    // Show loading
    if (content) content.innerHTML = '';
    if (loading) loading.removeAttribute('hidden');
    drawer.show();

    const html = await this.fetchProductQuickShop(productHandle, true);
    if (!html) {
      if (loading) loading.setAttribute('hidden', '');
      return;
    }

    if (loading) loading.setAttribute('hidden', '');

    if (content) {
      content.innerHTML = html.outerHTML;

      // Re-init Alpine for x-data directives
      if (window.Alpine) Alpine.initTree(content);


      // Enable drag-to-scroll on image gallery
      this.initGalleryDrag(content);

      // Re-init Yotpo
      setTimeout(() => {
        if (typeof yotpoWidgetsContainer !== 'undefined' && yotpoWidgetsContainer.initWidgets) {
          yotpoWidgetsContainer.initWidgets();
        }
      }, 300);
    }

    // Handle variation clicks inside drawer (registered once to avoid duplicates)
    if (!this._variationListenerAdded) {
      drawer.addEventListener('click', this._boundVariationClick);
      this._variationListenerAdded = true;
    }

    // Handle size radio changes inside drawer (registered once to avoid duplicates)
    if (!this._sizeListenerAdded) {
      drawer.addEventListener('change', this._boundSizeChange);
      this._sizeListenerAdded = true;
    }
  }

  async handleVariationClick(e) {
    const link = e.target.closest('[data-qs-variation-handle]');
    if (!link) return;

    e.preventDefault();

    const handle = link.dataset.qsVariationHandle;
    await this.updateDrawerVariation(handle, link);
  }

  async updateDrawerVariation(handle, link) {
    const drawer = document.getElementById('quick-shop-drawer');
    const content = drawer?.querySelector('[data-qs-content]');
    const regions = content?.querySelectorAll('[data-qs-region]');

    if (!content || !regions?.length) return this.openDrawer(handle);

    if (link.querySelector('.dot-pulse')) return;

    this.markSelectedVariation(content, link);

    const token = (this._qsUpdateToken = Symbol());
    const root = content.querySelector('.product-card-quick-shop-section');
    content.classList.add('qs-updating');

    let fresh;
    try {
      fresh = await this.fetchProductQuickShop(handle, true);
    } catch (err) {
      fresh = null;
    }
    if (this._qsUpdateToken !== token) return; 
    if (!root?.isConnected) return;

    if (!fresh) {
      content.classList.remove('qs-updating');
      return this.openDrawer(handle);
    }

    const oldRating = content.querySelector('[data-qs-region="rating"]');
    const ratingHeight = oldRating ? oldRating.offsetHeight : 0;
    const oldStars = oldRating?.querySelector('.yotpo-widget-instance');
    const starsWidth = oldStars ? oldStars.offsetWidth : 0;

    ['header', 'rating', 'buybox'].forEach((name) => {
      const oldEl = content.querySelector(`[data-qs-region="${name}"]`);
      const newEl = fresh.querySelector(`[data-qs-region="${name}"]`);
      if (oldEl && newEl) oldEl.replaceWith(newEl);
    });

    const newRating = content.querySelector('[data-qs-region="rating"]');
    if (newRating && ratingHeight) newRating.style.minHeight = `${ratingHeight}px`;
    const newStars = newRating?.querySelector('.yotpo-widget-instance');
    if (newStars && starsWidth) newStars.style.minWidth = `${starsWidth}px`;

    const oldGallery = content.querySelector('[data-qs-region="gallery"]');
    const newGallery = fresh.querySelector('[data-qs-region="gallery"]');
    if (oldGallery && newGallery) {
      oldGallery.replaceChildren(...newGallery.children);
      oldGallery.querySelectorAll('img').forEach((img) => img.setAttribute('draggable', 'false'));
      oldGallery.scrollLeft = 0;
      oldGallery.dispatchEvent(new Event('scroll'));
    }

    content.classList.remove('qs-updating');

    if (typeof yotpoWidgetsContainer !== 'undefined' && yotpoWidgetsContainer.initWidgets) {
      yotpoWidgetsContainer.initWidgets();
    }
  }

  markSelectedVariation(content, link) {
    content.querySelectorAll('[data-qs-variation-handle]').forEach((a) => {
      const isSelected = a === link;
      a.classList.toggle('border', isSelected);
      a.classList.toggle('border-black', isSelected);
      a.classList.toggle('p-[4px]', isSelected);
      a.classList.toggle('p-[2px]', !isSelected);
      a.classList.toggle('hover:border', !isSelected);
      a.classList.toggle('hover:border-black', !isSelected);
      const dot = a.querySelector('.dot-pulse');
      if (isSelected && !dot) {
        const span = document.createElement('span');
        span.className = 'absolute top-0.5 left-0.5 inline-flex h-2 w-2 rounded-full bg-black dot-pulse z-10';
        a.prepend(span);
      } else if (!isSelected && dot) {
        dot.remove();
      }
    });
  }

  initGalleryDrag(content) {
    const gallery = content.querySelector('[id^="qs-gallery-"]');
    if (!gallery) return;

    let isDown = false;
    let isDragging = false;
    let startX = 0;
    let scrollLeft = 0;

    // Prevent native browser image drag
    gallery.querySelectorAll('img').forEach((img) => {
      img.setAttribute('draggable', 'false');
    });
    gallery.addEventListener('dragstart', (e) => e.preventDefault());

    gallery.addEventListener('mousedown', (e) => {
      isDown = true;
      isDragging = false;
      startX = e.pageX;
      scrollLeft = gallery.scrollLeft;
    });

    gallery.addEventListener('mouseleave', () => {
      isDown = false;
      isDragging = false;
      gallery.style.cursor = '';
      gallery.style.userSelect = '';
    });

    gallery.addEventListener('mouseup', () => {
      isDown = false;
      isDragging = false;
      gallery.style.cursor = '';
      gallery.style.userSelect = '';
    });

    gallery.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      isDragging = true;
      e.preventDefault();
      gallery.style.cursor = 'grabbing';
      gallery.style.userSelect = 'none';
      const dx = e.pageX - startX;
      requestAnimationFrame(() => {
        gallery.scrollLeft = scrollLeft - dx;
      });
    });
  }

  handleSizeChange(e) {
    const input = e.target;
    if (!input.matches('input[data-option-position]')) return;

    const drawer = document.getElementById('quick-shop-drawer');
    const content = drawer?.querySelector('[data-qs-content]');
    if (!content) return;

    const variantsEl = content.querySelector('[data-qs-variants]');
    if (!variantsEl) return;
    const variants = JSON.parse(variantsEl.textContent);

    // Collect all selected option values
    const selectedOptions = {};
    content.querySelectorAll('input[data-option-position]:checked').forEach((inp) => {
      selectedOptions[parseInt(inp.dataset.optionPosition)] = inp.value;
    });

    // Find matching variant
    const match = variants.find((v) =>
      Object.entries(selectedOptions).every(([pos, val]) => v['option' + pos] === val)
    );
    if (!match) return;

    // Update hidden form variant ID
    const formId = input.getAttribute('form');
    const form = document.forms[formId];
    if (form) form.id.value = match.id;

    // Update SIZE: X label
    const sizeLabel = content.querySelector('[data-qs-size-label]');
    if (sizeLabel) sizeLabel.textContent = input.value;

    // Update border styling on size cells
    content.querySelectorAll(`input[data-option-position="${input.dataset.optionPosition}"]`).forEach((inp) => {
      const lbl = inp.closest('label');
      if (!lbl) return;
      if (inp === input) {
        lbl.classList.remove('border-[#DEDEDE]');
        lbl.classList.add('border-black');
      } else {
        lbl.classList.remove('border-black');
        lbl.classList.add('border-[#DEDEDE]');
      }
    });
  }

  async fetchProductQuickShop(productHandle, returnElement = false) {
    // Only use cache for prefetch (returnElement = false).
    // When opening the drawer (returnElement = true), always fetch fresh
    // so the correct variant state (available/sold out) is rendered.
    if (!returnElement && window.quickShopHtmls[productHandle]) {
      return null;
    }

    const url = new URL(`${Shopify.routes.root}products/${productHandle}`, window.location.origin);
    url.searchParams.append('section_id', this.productSectionId);

    const response = await fetch(url);
    const responseHtml = await response.text();
    const newContent = new DOMParser().parseFromString(responseHtml, 'text/html');
    const quickShopElement = newContent.querySelector('.product-card-quick-shop-section');

    if (quickShopElement) {
      window.quickShopHtmls[productHandle] = quickShopElement.outerHTML;
    }

    return returnElement ? quickShopElement : null;
  }
}

customElements.define('product-card-quick-shop', CardQuickShop);
